import React from 'react';
import Icon from '../../../components/AppIcon';

const DataQualityDashboard = () => {
  const qualityMetrics = [
    {
      id: 1,
      title: "Profils incomplets",
      count: 1247,
      percentage: 9.7,
      icon: "AlertCircle",
      color: "var(--color-warning)",
      action: "Compléter les données",
      priority: "medium"
    },
    {
      id: 2,
      title: "Doublons potentiels",
      count: 247,
      percentage: 1.9,
      icon: "Copy",
      color: "var(--color-error)",
      action: "Fusionner les contacts",
      priority: "high"
    },
    {
      id: 3,
      title: "Conformité RGPD",
      count: 11893,
      percentage: 92.6,
      icon: "Shield",
      color: "var(--color-success)",
      action: "Vérifier les consentements",
      priority: "low"
    },
    {
      id: 4,
      title: "Emails invalides",
      count: 342,
      percentage: 2.7,
      icon: "MailX",
      color: "var(--color-error)",
      action: "Valider les emails",
      priority: "high"
    },
    {
      id: 5,
      title: "Téléphones manquants",
      count: 2156,
      percentage: 16.8,
      icon: "PhoneOff",
      color: "var(--color-warning)",
      action: "Ajouter les numéros",
      priority: "medium"
    },
    {
      id: 6,
      title: "Dernière mise à jour",
      count: 847,
      percentage: 6.6,
      icon: "Clock",
      color: "var(--color-secondary)",
      action: "Actualiser les données",
      priority: "low"
    }
  ];

  const recommendations = [
    {
      id: 1,
      title: "Campagne de mise à jour",
      description: "Lancer une campagne email pour compléter 1247 profils incomplets",
      impact: "Amélioration de 9.7% de la qualité",
      icon: "Mail",
      color: "var(--color-primary)"
    },
    {
      id: 2,
      title: "Fusion automatique",
      description: "Utiliser l'IA pour fusionner 247 doublons détectés",
      impact: "Réduction de 1.9% des redondances",
      icon: "Zap",
      color: "var(--color-accent)"
    },
    {
      id: 3,
      title: "Validation en masse",
      description: "Vérifier 342 adresses email invalides via service externe",
      impact: "Amélioration de 2.7% de la délivrabilité",
      icon: "CheckCircle",
      color: "var(--color-success)"
    }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-error/10 text-error';
      case 'medium':
        return 'bg-warning/10 text-warning';
      case 'low':
        return 'bg-success/10 text-success';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high':
        return 'Haute';
      case 'medium':
        return 'Moyenne';
      case 'low':
        return 'Basse';
      default:
        return 'Normal';
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="bg-card rounded-lg p-4 md:p-6 shadow-elevation-2">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">
              Qualité des données
            </h2>
            <p className="text-sm text-muted-foreground">
              Surveillance et recommandations
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-success/10 rounded-lg">
            <Icon name="TrendingUp" size={16} color="var(--color-success)" />
            <span className="text-sm font-semibold text-success">92.3%</span>
          </div>
        </div>

        <div className="space-y-3 md:space-y-4">
          {qualityMetrics?.map((metric) => (
            <div
              key={metric?.id}
              className="flex items-center justify-between p-3 md:p-4 bg-muted rounded-lg hover:bg-muted/80 transition-smooth"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${metric?.color}15` }}
                >
                  <Icon name={metric?.icon} size={18} color={metric?.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-medium text-foreground line-clamp-1">
                      {metric?.title}
                    </h3>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(metric?.priority)}`}>
                      {getPriorityLabel(metric?.priority)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-1">
                    {metric?.action}
                  </p>
                </div>
              </div>
              
              <div className="text-right flex-shrink-0 ml-4">
                <p className="text-lg md:text-xl font-semibold text-foreground whitespace-nowrap">
                  {metric?.count?.toLocaleString('fr-FR')}
                </p>
                <p className="text-xs text-muted-foreground whitespace-nowrap">
                  {metric?.percentage}%
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card rounded-lg p-4 md:p-6 shadow-elevation-2">
        <div className="flex items-center gap-2 mb-4 md:mb-6">
          <Icon name="Lightbulb" size={20} color="var(--color-accent)" />
          <h3 className="text-base md:text-lg font-semibold text-foreground">
            Recommandations intelligentes
          </h3>
        </div>

        <div className="space-y-3 md:space-y-4">
          {recommendations?.map((rec) => (
            <div
              key={rec?.id}
              className="p-3 md:p-4 border border-border rounded-lg hover:border-primary/50 transition-smooth"
            >
              <div className="flex items-start gap-3 mb-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${rec?.color}15` }}
                >
                  <Icon name={rec?.icon} size={16} color={rec?.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-foreground mb-1">
                    {rec?.title}
                  </h4>
                  <p className="text-xs text-muted-foreground mb-2">
                    {rec?.description}
                  </p>
                  <div className="flex items-center gap-2">
                    <Icon name="Target" size={12} color="var(--color-success)" />
                    <span className="text-xs text-success font-medium">
                      {rec?.impact}
                    </span>
                  </div>
                </div>
              </div>
              
              <button className="w-full px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth">
                Appliquer la recommandation
              </button>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-4 md:p-6 border border-primary/20">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
            <Icon name="Sparkles" size={20} color="var(--color-primary)" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-foreground mb-2">
              Enrichissement automatique disponible
            </h4>
            <p className="text-xs text-muted-foreground mb-3">
              Utilisez l'IA pour enrichir automatiquement 3,245 profils avec des données professionnelles et sociales
            </p>
            <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth">
              Activer l'enrichissement
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataQualityDashboard;